export const PODCAST_CHAPTERS_JSON_VERSION = "1.2.0";
export const MAX_CHAPTERS_PER_EPISODE = 100;
export const MAX_CHAPTER_TITLE_LENGTH = 200;
export const MAX_CHAPTER_URL_LENGTH = 2000;
export const MAX_CHAPTER_IMAGE_URL_LENGTH = 2000;

export type EpisodeChapterSource =
  | "scroll"
  | "podcast:chapters"
  | "description"
  | "embedded";

export interface EpisodeChapter {
  startTimeMs: number;
  endTimeMs?: number | null;
  title: string;
  imageUrl?: string | null;
  url?: string | null;
  source: EpisodeChapterSource;
}

const TIMESTAMP_RE =
  /^\s*[\[(]?\s*(\d{1,3}):(\d{2})(?::(\d{2}))?(?:[.,](\d{1,3}))?\s*[\])]?/;

const SOURCE_PRIORITY: Record<EpisodeChapterSource, number> = {
  scroll: 0,
  "podcast:chapters": 1,
  embedded: 2,
  description: 3,
};

function roundFiniteMs(value: number): number | null {
  const rounded = Math.round(value);
  return Number.isFinite(rounded) && rounded >= 0 ? rounded : null;
}

function secondsToFiniteMs(value: number): number | null {
  return roundFiniteMs(value * 1000);
}

export function parseChapterTimestamp(input: unknown): number | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("-")) return null;

  if (/^\d+$/.test(trimmed)) {
    const seconds = parseInt(trimmed, 10);
    return seconds * 1000;
  }
  if (/^\d+\.\d+$/.test(trimmed)) {
    const seconds = parseFloat(trimmed);
    return Math.round(seconds * 1000);
  }

  const match = TIMESTAMP_RE.exec(trimmed);
  if (!match) return null;
  const a = parseInt(match[1], 10);
  const b = parseInt(match[2], 10);
  const c = match[3] != null ? parseInt(match[3], 10) : null;
  const fractional = match[4] != null ? parseFloat(`0.${match[4]}`) : 0;
  if (b >= 60 || (c != null && c >= 60)) return null;
  const totalSeconds = c != null ? a * 3600 + b * 60 + c : a * 60 + b;
  return Math.round((totalSeconds + fractional) * 1000);
}

export function formatChapterTimestamp(ms: unknown): string {
  let value = typeof ms === "number" ? ms : NaN;
  if (!Number.isFinite(value) || value < 0) value = 0;
  const totalSeconds = Math.floor(value / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

function clampTitle(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > MAX_CHAPTER_TITLE_LENGTH
    ? trimmed.slice(0, MAX_CHAPTER_TITLE_LENGTH)
    : trimmed;
}

function clampOptionalUrl(value: unknown, max: number): string | null {
  if (value == null) return null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.length > max) return null;
  return trimmed;
}

function finalizeChapters(chapters: EpisodeChapter[]): EpisodeChapter[] {
  chapters.sort((a, b) => {
    if (a.startTimeMs !== b.startTimeMs) return a.startTimeMs - b.startTimeMs;
    return SOURCE_PRIORITY[a.source] - SOURCE_PRIORITY[b.source];
  });
  const seen = new Set<number>();
  const unique: EpisodeChapter[] = [];
  for (const c of chapters) {
    if (seen.has(c.startTimeMs)) continue;
    seen.add(c.startTimeMs);
    unique.push(c);
  }
  return unique.slice(0, MAX_CHAPTERS_PER_EPISODE);
}

export function normalizeScrollChapters(input: unknown): EpisodeChapter[] {
  if (!Array.isArray(input)) return [];
  const out: EpisodeChapter[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const obj = raw as Record<string, unknown>;

    let startTimeMs: number | null = null;
    if (
      typeof obj.startTimeMs === "number" &&
      Number.isFinite(obj.startTimeMs) &&
      obj.startTimeMs >= 0
    ) {
      startTimeMs = roundFiniteMs(obj.startTimeMs);
    } else if (typeof obj.timestamp === "string") {
      startTimeMs = parseChapterTimestamp(obj.timestamp);
    } else if (
      typeof obj.timestamp === "number" &&
      Number.isFinite(obj.timestamp) &&
      obj.timestamp >= 0
    ) {
      startTimeMs = secondsToFiniteMs(obj.timestamp);
    } else if (
      typeof obj.startTime === "number" &&
      Number.isFinite(obj.startTime) &&
      obj.startTime >= 0
    ) {
      startTimeMs = secondsToFiniteMs(obj.startTime);
    }
    if (startTimeMs == null || !Number.isFinite(startTimeMs) || startTimeMs < 0) {
      continue;
    }

    const title = clampTitle(obj.title ?? obj.label);
    if (!title) continue;

    let endTimeMs: number | null = null;
    if (
      typeof obj.endTimeMs === "number" &&
      Number.isFinite(obj.endTimeMs) &&
      obj.endTimeMs > startTimeMs
    ) {
      endTimeMs = roundFiniteMs(obj.endTimeMs);
    } else if (
      typeof obj.endTime === "number" &&
      Number.isFinite(obj.endTime) &&
      obj.endTime * 1000 > startTimeMs
    ) {
      endTimeMs = secondsToFiniteMs(obj.endTime);
    }

    const sourceCandidate = typeof obj.source === "string" ? obj.source : null;
    const source: EpisodeChapterSource =
      sourceCandidate === "podcast:chapters" ||
      sourceCandidate === "description" ||
      sourceCandidate === "embedded"
        ? sourceCandidate
        : "scroll";

    out.push({
      startTimeMs,
      endTimeMs,
      title,
      imageUrl: clampOptionalUrl(obj.imageUrl ?? obj.img, MAX_CHAPTER_IMAGE_URL_LENGTH),
      url: clampOptionalUrl(obj.url, MAX_CHAPTER_URL_LENGTH),
      source,
    });
  }
  return finalizeChapters(out);
}

export function normalizePodcastChaptersJson(input: unknown): EpisodeChapter[] {
  if (!input || typeof input !== "object") return [];
  const root = input as Record<string, unknown>;
  const chapters = root.chapters;
  if (!Array.isArray(chapters)) return [];
  const out: EpisodeChapter[] = [];
  for (const raw of chapters) {
    if (!raw || typeof raw !== "object") continue;
    const obj = raw as Record<string, unknown>;

    if (
      typeof obj.startTime !== "number" ||
      !Number.isFinite(obj.startTime) ||
      obj.startTime < 0
    ) {
      continue;
    }
    const startTimeMs = Math.round(obj.startTime * 1000);

    const title = clampTitle(obj.title);
    if (!title) continue;

    let endTimeMs: number | null = null;
    if (
      typeof obj.endTime === "number" &&
      Number.isFinite(obj.endTime) &&
      obj.endTime * 1000 > startTimeMs
    ) {
      endTimeMs = Math.round(obj.endTime * 1000);
    }

    out.push({
      startTimeMs,
      endTimeMs,
      title,
      imageUrl: clampOptionalUrl(obj.img, MAX_CHAPTER_IMAGE_URL_LENGTH),
      url: clampOptionalUrl(obj.url, MAX_CHAPTER_URL_LENGTH),
      source: "podcast:chapters",
    });
  }
  return finalizeChapters(out);
}

const CHAPTER_TAG_RE = /<podcast:chapters\b([^>]*?)\/?>/i;
const ATTR_URL_RE = /\burl\s*=\s*(?:"([^"]*)"|'([^']*)')/i;
const ATTR_TYPE_RE = /\btype\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

function decodeXmlAttribute(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => {
      const codePoint = parseInt(hex, 16);
      if (!Number.isFinite(codePoint)) return "";
      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return "";
      }
    })
    .replace(/&#(\d+);/g, (_, dec: string) => {
      const codePoint = parseInt(dec, 10);
      if (!Number.isFinite(codePoint)) return "";
      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return "";
      }
    });
}

export function extractChapterTagFromItemXml(
  itemXml: unknown,
): { url: string; type?: string } | null {
  if (typeof itemXml !== "string" || !itemXml) return null;
  const tagMatch = CHAPTER_TAG_RE.exec(itemXml);
  if (!tagMatch) return null;
  const attrs = tagMatch[1] || "";
  const urlMatch = ATTR_URL_RE.exec(attrs);
  if (!urlMatch) return null;
  const url = decodeXmlAttribute(urlMatch[1] ?? urlMatch[2] ?? "").trim();
  if (!url) return null;
  const typeMatch = ATTR_TYPE_RE.exec(attrs);
  const type = typeMatch
    ? decodeXmlAttribute(typeMatch[1] ?? typeMatch[2] ?? "").trim()
    : "";
  return type ? { url, type } : { url };
}

export function normalizeEpisodeChapters(
  input: unknown,
  sourceHint?: EpisodeChapterSource,
): EpisodeChapter[] {
  if (input == null) return [];
  if (Array.isArray(input)) {
    const result = normalizeScrollChapters(input);
    if (sourceHint) return result.map((c) => ({ ...c, source: sourceHint }));
    return result;
  }
  if (typeof input === "object") {
    const obj = input as Record<string, unknown>;
    if (Array.isArray(obj.chapters)) {
      const result = normalizePodcastChaptersJson(input);
      if (sourceHint) return result.map((c) => ({ ...c, source: sourceHint }));
      return result;
    }
  }
  return [];
}

export function getCurrentChapter(
  chapters: EpisodeChapter[],
  positionMs: number,
): EpisodeChapter | null {
  if (!Array.isArray(chapters) || chapters.length === 0) return null;
  if (!Number.isFinite(positionMs) || positionMs < 0) return null;
  let current: EpisodeChapter | null = null;
  for (const c of chapters) {
    if (c.startTimeMs <= positionMs) current = c;
    else break;
  }
  return current;
}

export function getNextChapter(
  chapters: EpisodeChapter[],
  positionMs: number,
): EpisodeChapter | null {
  if (!Array.isArray(chapters) || chapters.length === 0) return null;
  if (!Number.isFinite(positionMs)) return null;
  for (const c of chapters) {
    if (c.startTimeMs > positionMs) return c;
  }
  return null;
}

export function getPreviousChapter(
  chapters: EpisodeChapter[],
  positionMs: number,
): EpisodeChapter | null {
  const current = getCurrentChapter(chapters, positionMs);
  if (!current) return null;
  const idx = chapters.indexOf(current);
  return idx > 0 ? chapters[idx - 1] : null;
}

/** Reject obvious SSRF vectors before fetching a chapter JSON URL.
 *  Returns a reason string when unsafe, or null when the URL passes the
 *  literal-host checks. Note: this does NOT do DNS resolution — callers
 *  that need DNS-rebinding protection should layer that on top. */
export function isUnsafeChapterUrl(
  url: string,
): "invalid-url" | "blocked-scheme" | "blocked-host" | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return "invalid-url";
  }
  if (parsed.protocol !== "https:") return "blocked-scheme";
  const host = parsed.hostname.toLowerCase().replace(/^\[|\]$/g, "");
  if (!host) return "blocked-host";
  if (host === "localhost" || host.endsWith(".localhost")) return "blocked-host";
  if (host === "0.0.0.0" || host === "::" || host === "::1") return "blocked-host";
  if (/^127\./.test(host)) return "blocked-host";
  if (/^10\./.test(host)) return "blocked-host";
  if (/^192\.168\./.test(host)) return "blocked-host";
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(host)) return "blocked-host";
  if (/^169\.254\./.test(host)) return "blocked-host";
  if (/^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(host)) return "blocked-host"; // CGNAT
  if (/^f[cd][0-9a-f]{2}:/i.test(host)) return "blocked-host"; // unique local IPv6
  if (/^fe80:/i.test(host)) return "blocked-host"; // link-local IPv6
  return null;
}

export interface PodcastChaptersJsonChapter {
  startTime: number;
  title: string;
  endTime?: number;
  img?: string;
  url?: string;
}

/** Convert normalized internal chapters into the Podcasting 2.0 chapters
 *  JSON wire format (seconds, omits empty fields). Inverse of
 *  normalizePodcastChaptersJson. */
export function toPodcastChaptersJson(
  chapters: EpisodeChapter[],
): PodcastChaptersJsonChapter[] {
  return chapters.map((c) => {
    const out: PodcastChaptersJsonChapter = {
      startTime: c.startTimeMs / 1000,
      title: c.title,
    };
    if (c.endTimeMs != null) out.endTime = c.endTimeMs / 1000;
    if (c.imageUrl) out.img = c.imageUrl;
    if (c.url) out.url = c.url;
    return out;
  });
}
